import React, { useEffect, useState } from 'react';
import { api } from '../api';

function stSecs(st) {
  if (!st) return 0;
  const p = st.split(':');
  return parseInt(p[0] || 0) * 3600 + parseInt(p[1] || 0) * 60 + parseInt(p[2] || 0);
}

function timingGroupKey(t) {
  const date = t.pctimestamp ? t.pctimestamp.substring(0, 10) : '';
  const bucket = Math.floor(stSecs(t.searchtime) / 120); // 2-minute buckets
  return `${t.clientid}|${date}|${bucket}`;
}

function formatTs(ts) {
  if (!ts) return '';
  const part = ts.split('T')[1];
  return part ? `${ts.substring(0, 10)}  ${part}` : ts;
}

export default function PortalTimings() {
  const [options, setOptions] = useState({ clientids: [], bookingTypes: [] });
  const [clientid, setClientid] = useState('');
  const [bookingType, setBookingType] = useState('');
  const [timings, setTimings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [dropsMap, setDropsMap] = useState({});
  const [photosMap, setPhotosMap] = useState({});
  const [photoPreview, setPhotoPreview] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');

  useEffect(() => {
    api.getPortalTimingOptions().then(opts => {
      setOptions(opts);
      if (opts.clientids.length === 1) {
        setClientid(opts.clientids[0]);
        setBookingType('Regular Weekly');
      }
    }).catch(console.error);
  }, []);

  useEffect(() => {
    if (!clientid) { setTimings([]); setDropsMap({}); return; }
    setLoading(true);
    api.getPortalTimings(clientid, bookingType)
      .then(setTimings)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [clientid, bookingType]);

  useEffect(() => {
    if (!clientid) return;
    api.getPortalTimingDrops(clientid).then(drops => {
      const dm = {}, pm = {};
      drops.forEach(d => {
        const key = `${d.clientid}|${d.date}|${d.bucket}`;
        dm[key] = d.availableShifts || [];
        if (d.photoUrl) pm[key] = d.photoUrl;
      });
      setDropsMap(dm);
      setPhotosMap(pm);
    }).catch(console.error);
  }, [clientid]);

  // Build group map: key -> { firstIdx, lastIdx, count }
  const groupFirstIdx = {};
  const groupLastIdx = {};
  const groupCount = {};
  timings.forEach((t, i) => {
    const key = timingGroupKey(t);
    if (!(key in groupFirstIdx)) groupFirstIdx[key] = i;
    groupLastIdx[key] = i;
    groupCount[key] = (groupCount[key] || 0) + 1;
  });

  function toggleExpand(key) {
    setExpanded(e => ({ ...e, [key]: !e[key] }));
  }

  async function handleSync() {
    setSyncing(true);
    setSyncMsg('');
    try {
      await api.syncPortalLogs();
      setSyncMsg('Synced!');
      if (clientid) {
        const [t, drops] = await Promise.all([
          api.getPortalTimings(clientid, bookingType),
          api.getPortalTimingDrops(clientid)
        ]);
        setTimings(t);
        const dm = {}, pm = {};
        drops.forEach(d => {
          const key = `${d.clientid}|${d.date}|${d.bucket}`;
          dm[key] = d.availableShifts || [];
          if (d.photoUrl) pm[key] = d.photoUrl;
        });
        setDropsMap(dm);
        setPhotosMap(pm);
      }
    } catch (err) { setSyncMsg(`Error: ${err.message}`); }
    finally { setSyncing(false); setTimeout(() => setSyncMsg(''), 4000); }
  }

  let lastDate = null;
  let band = false;
  const DATE_COLORS = ['#ffffff', '#ebf4ff'];

  return (
    <div>
      <div className="card" style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        {options.clientids.length > 1 && (
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Property</label>
            <select value={clientid} onChange={e => {
              const val = e.target.value;
              setClientid(val);
              setBookingType(val ? 'Regular Weekly' : '');
            }} style={{ minWidth: 160 }}>
              <option value="">— select —</option>
              {options.clientids.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Booking Type</label>
          <select value={bookingType} onChange={e => setBookingType(e.target.value)} style={{ minWidth: 160 }} disabled={!clientid}>
            <option value="">All</option>
            {options.bookingTypes.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={handleSync} disabled={syncing} style={{ alignSelf: 'flex-end' }}>
          {syncing ? 'Syncing...' : 'Refresh'}
        </button>
        {syncMsg && <span style={{ fontSize: 13, color: syncMsg.startsWith('Error') ? '#e53e3e' : '#38a169' }}>{syncMsg}</span>}
        {timings.length > 0 && (
          <span style={{ color: '#718096', fontSize: 13, marginLeft: 'auto' }}>{timings.length} records</span>
        )}
      </div>

      {!clientid && !loading && <div className="card" style={{ color: '#718096' }}>Select a property to view timings.</div>}
      {loading && <div className="card" style={{ color: '#718096' }}>Loading...</div>}
      {!loading && clientid && timings.length === 0 && <div className="card" style={{ color: '#718096' }}>No timing records found.</div>}

      {!loading && timings.length > 0 && (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ minWidth: 650 }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Client ID</th>
                <th>Timing</th>
                <th>Return</th>
                <th>Search Time</th>
                <th>PC Timestamp</th>
                <th style={{ textAlign: 'center' }}>Total # of Shifts</th>
                <th style={{ textAlign: 'center' }}>Drops</th>
              </tr>
            </thead>
            <tbody>
              {timings.map((t, i) => {
                const dateStr = t.pctimestamp ? t.pctimestamp.substring(0, 10) : '';
                if (dateStr !== lastDate) { band = !band; lastDate = dateStr; }
                const bg = DATE_COLORS[band ? 1 : 0];
                const key = timingGroupKey(t);
                const isFirst = i === groupFirstIdx[key];
                const isLast = i === groupLastIdx[key];
                const count = groupCount[key];
                const isExp = expanded[key];
                const drops = dropsMap[key] || [];
                const totalShifts = drops.reduce((s, d) => s + (d.count || 0), 0);
                return (
                  <React.Fragment key={String(t._id)}>
                    <tr
                      style={{ background: bg, cursor: 'pointer', borderTop: isFirst && i > 0 ? '2px solid #cbd5e0' : undefined }}
                      onClick={() => toggleExpand(key)}
                    >
                      <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{dateStr}</td>
                      <td>{t.clientid}</td>
                      <td>{t.timing}</td>
                      <td>{t['return']}</td>
                      <td>{t.searchtime}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{formatTs(t.pctimestamp)}</td>
                      <td style={{ textAlign: 'center', fontWeight: totalShifts > 0 ? 600 : 400, color: totalShifts > 0 ? '#276749' : '#a0aec0' }}>
                        {totalShifts > 0 ? totalShifts : '—'}
                      </td>
                      {isFirst && (
                        <td rowSpan={count} style={{ textAlign: 'center', verticalAlign: 'middle', background: bg }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                            <span style={{ background: drops.length ? '#c6f6d5' : '#e2e8f0', color: drops.length ? '#276749' : '#718096', borderRadius: 10, padding: '2px 8px', fontSize: 12, whiteSpace: 'nowrap' }}>
                              {drops.length} {isExp ? '▲' : '▼'}
                            </span>
                            {photosMap[key] && (
                              <a
                                href={(process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : '') + photosMap[key]}
                                target="_blank"
                                rel="noreferrer"
                                style={{ fontSize: 14, lineHeight: 1, textDecoration: 'none' }}
                                onMouseEnter={e => { e.stopPropagation(); setPhotoPreview({ url: photosMap[key], x: e.clientX, y: e.clientY }); }}
                                onMouseMove={e => setPhotoPreview(p => p ? { ...p, x: e.clientX, y: e.clientY } : p)}
                                onMouseLeave={() => setPhotoPreview(null)}
                                onClick={e => e.stopPropagation()}
                              >📷</a>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                    {isLast && isExp && (
                      <tr style={{ background: '#f0fff4' }}>
                        <td colSpan={8} style={{ padding: '10px 20px 14px' }}>
                          {drops.length > 0 ? (
                            <table style={{ fontSize: 13, width: 'auto' }}>
                              <thead>
                                <tr>
                                  <th style={{ textAlign: 'left', paddingRight: 20, paddingBottom: 4 }}>Date</th>
                                  <th style={{ textAlign: 'left', paddingRight: 20, paddingBottom: 4 }}>Meal Period</th>
                                  <th style={{ textAlign: 'left', paddingRight: 20, paddingBottom: 4 }}>Start</th>
                                  <th style={{ textAlign: 'left', paddingRight: 20, paddingBottom: 4 }}>End</th>
                                  <th style={{ textAlign: 'left', paddingRight: 20, paddingBottom: 4 }}>Available</th>
                                </tr>
                              </thead>
                              <tbody>
                                {drops.map((d, j) => (
                                  <tr key={j}>
                                    <td style={{ paddingRight: 20 }}>{d.date}</td>
                                    <td style={{ paddingRight: 20 }}>{d.mealPeriod}</td>
                                    <td style={{ paddingRight: 20 }}>{d.startTime || '—'}</td>
                                    <td style={{ paddingRight: 20 }}>{d.endTime || '—'}</td>
                                    <td style={{ paddingRight: 20, fontWeight: 600 }}>{d.count}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <span style={{ color: '#718096', fontSize: 13 }}>No shift drops logged for this search.</span>
                          )}
                          {photosMap[key] && (
                            <div style={{ marginTop: 10 }} onClick={e => e.stopPropagation()}>
                              <a href={(process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : '') + photosMap[key]} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: '#2b6cb0' }}>📷 View Photo</a>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {photoPreview && (
        <div style={{ position: 'fixed', left: photoPreview.x - 8, top: 20, transform: 'translateX(-100%)', zIndex: 9999, pointerEvents: 'none', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.2)', padding: 4 }}>
          <img src={(process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : '') + photoPreview.url} alt="preview" style={{ maxHeight: 'calc(100vh - 40px)', maxWidth: 500, width: 'auto', display: 'block', borderRadius: 4 }} />
        </div>
      )}
    </div>
  );
}
